import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import {Observable, Subscription} from 'rxjs';

import {TFirebaseUser} from '@chancer/common/lib/interfaces/firestore/FirestoreClientInterfaces';
import {
  LeaderboardType,
  TMultiGameLeader,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {
  IColorConfiguration,
  getColorConfiguration,
} from '@chancer/common/lib/utils/ColorUtils';
import {LeaderboardAvatar} from '../Avatar/LeaderboardAvatar';
import {LabelBadge} from '../Status/LabelBadge';
import {
  COLOR_DARK,
  COLOR_GREEN,
  COLOR_GREY_3,
  COLOR_GREY_6,
  COLOR_LIGHT,
  COLOR_RED,
  COLOR_YELLOW,
  FONT_COPY,
  FONT_COPY_BOLD,
  FONT_COPY_MEDIUM,
  FONT_SUB_TITLE,
  FONT_SIZE_NAV,
  FONT_SIZE_META,
} from '../Styles/DesignSystem-chancer';
import {CompositedViewStyle} from '../Styles/StyleTypes';
import {
  AchievementWinnerIcon,
  LeaderDownArrowIcon,
  LeaderUpArrowIcon,
  MissingUserAvatar,
} from '../Svg/SvgIcons';
import {getTagline} from './EntrantItem';
import {MultiGameScore} from './MultiGameScore';
import {WinnerEntrantItemWrapper} from './WinnerEntrantItemWrapper';

export interface IMultiGameEntrantItemProps {
  visible?: boolean;
  type: LeaderboardType;
  position: number;
  player: TMultiGameLeader;
  winner: boolean;
  currentUser: TFirebaseUser | null;
  isFollowing: boolean;
  average?: number;
  colorConfig?: IColorConfiguration;
  getUserById: (userId: string) => Observable<TFirebaseUser>;
  onPress?: (user: TFirebaseUser) => void;
}

export const ITEM_HEIGHT = 66;

export const MultiGameEntrantItem: React.FC<IMultiGameEntrantItemProps> = (
  props,
) => {
  const {
    getUserById,
    colorConfig = getColorConfiguration(undefined),
    onPress,
  } = props;
  const visible = props.visible ?? true;
  const userId = props.player.u;
  const isCurrentUser = props.currentUser !== null;

  const [user, setUser] = useState<TFirebaseUser | null>(props.currentUser);
  const [userFailed, setUserFailed] = useState(false);

  // Deal with other users
  useEffect(() => {
    let sub: Subscription | undefined;
    if (visible && user === null) {
      sub = getUserById(userId).subscribe({
        next: (u) => {
          setUser(u);
          if (u.id === '') {
            setUserFailed(true);
          }
        },
      });
    }
    return () => {
      if (sub) {
        sub.unsubscribe();
      }
    };
  }, [visible, userId, user, getUserById]);

  const contextualContainer: CompositedViewStyle = useMemo(
    () => [
      styles.container,
      isCurrentUser
        ? {backgroundColor: colorConfig.leaderboardUsersItemBackground}
        : {backgroundColor: colorConfig.leaderboardItemsBackground},
    ],
    [colorConfig, isCurrentUser],
  );

  const _onPress = useCallback(() => {
    if (user !== null && onPress) {
      onPress(user);
    }
  }, [user, onPress]);

  return (
    <WinnerEntrantItemWrapper isWinner={props.winner}>
      <Pressable
        style={contextualContainer}
        disabled={onPress === undefined || userFailed}
        onPress={_onPress}>
        {visible && (
          <>
            <View style={styles.positionContainer}>
              <Text style={styles.position}>{props.position}</Text>
              <View style={styles.arrowContainer}>
                {!userFailed &&
                  props.player.d !== undefined &&
                  props.player.d > 0 && (
                    <LeaderUpArrowIcon
                      color={COLOR_GREEN}
                      width={10}
                      height={14}
                    />
                  )}
                {props.player.d !== undefined && props.player.d < 0 && (
                  <LeaderDownArrowIcon
                    color={COLOR_RED}
                    width={10}
                    height={14}
                  />
                )}
              </View>
            </View>
            {userFailed ? (
              <>
                <View style={styles.missingAvatarContainer}>
                  <MissingUserAvatar color={COLOR_GREY_3} />
                </View>
                <Text style={styles.missingUserCopy}>
                  Dropped out of the season race
                </Text>
              </>
            ) : (
              <>
                <LeaderboardAvatar
                  user={user}
                  isFollowing={props.isFollowing}
                  isCurrentUser={isCurrentUser}
                />

                <View style={styles.copyContainer}>
                  <View style={styles.nameContainer}>
                    {user && (
                      <>
                        <Text
                          style={styles.name}
                          numberOfLines={1}
                          ellipsizeMode="tail">
                          {user?.name}
                        </Text>
                        {isCurrentUser && (
                          <LabelBadge
                            labelColor={COLOR_DARK}
                            backgroundColor={COLOR_YELLOW}
                            label="YOU"
                            style={styles.currentUserBadge}
                          />
                        )}
                        {props.player.f !== undefined && props.player.f > 0 && (
                          <>
                            <View style={styles.achievementIcon}>
                              <AchievementWinnerIcon
                                color={COLOR_GREY_6}
                                width={12}
                                height={14}
                              />
                            </View>
                            <Text style={styles.achievementValue}>
                              {props.player.f}
                            </Text>
                          </>
                        )}
                        {props.average !== undefined && (
                          <>
                            <Text style={styles.achievementLabel}>Avg</Text>
                            <Text style={styles.achievementValue}>
                              {props.average}
                            </Text>
                          </>
                        )}
                      </>
                    )}
                  </View>
                  <Text
                    style={styles.tagline}
                    numberOfLines={1}
                    ellipsizeMode="tail">
                    {getTagline(user)}
                  </Text>
                </View>
                <View style={styles.multiGameScoreContainer}>
                  <MultiGameScore
                    score={
                      props.type && props.type.startsWith('allstar_')
                        ? props.player.t ?? 0
                        : props.player.s
                    }
                  />
                </View>
              </>
            )}
          </>
        )}
      </Pressable>
    </WinnerEntrantItemWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    userSelect: 'none',
    position: 'relative',
    height: 64,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 8,
    marginTop: 2,
  },
  positionContainer: {
    marginTop: 13,
    marginBottom: 16,
    alignSelf: 'flex-start',
  },
  position: {
    flexShrink: 0,
    color: COLOR_LIGHT,
    fontFamily: FONT_COPY_BOLD,
    fontSize: FONT_SIZE_NAV,
    width: 36,
    textAlign: 'center',
    alignSelf: 'flex-start',
  },
  arrowContainer: {
    width: 36,
    alignItems: 'center',
  },
  copyContainer: {
    flex: 1,
    flexGrow: 1,
    flexDirection: 'column',
    alignSelf: 'flex-start',
    marginLeft: 12,
    overflow: 'hidden',
  },
  nameContainer: {
    flexDirection: 'row',
    marginTop: 13,
    overflow: 'hidden',
    alignItems: 'center',
  },
  name: {
    color: COLOR_LIGHT,
    fontFamily: FONT_SUB_TITLE,
    fontSize: FONT_SIZE_NAV,
    marginRight: 8,
    lineHeight: 22,
  },
  currentUserBadge: {alignSelf: 'flex-end', marginRight: 8, marginBottom: 2},
  achievementIcon: {
    marginTop: 2,
    marginLeft: 6,
  },
  achievementLabel: {
    marginLeft: 6,
    fontFamily: FONT_COPY_MEDIUM,
    fontSize: 14,
    color: COLOR_GREY_6,
  },
  achievementValue: {
    flexGrow: 0,
    marginLeft: 2,
    fontFamily: FONT_COPY_MEDIUM,
    fontSize: 14,
    color: COLOR_GREY_6,
  },
  tagline: {
    color: COLOR_GREY_6,
    fontFamily: FONT_COPY_MEDIUM,
    fontSize: FONT_SIZE_META,
    marginRight: 4,
  },
  multiGameScoreContainer: {
    flexShrink: 0,
    textAlign: 'center',
    marginTop: 13,
    alignSelf: 'flex-start',
  },
  missingAvatarContainer: {
    marginLeft: 2,
  },
  missingUserCopy: {
    marginLeft: 12,
    color: COLOR_GREY_3,
    fontFamily: FONT_COPY,
    fontSize: 14,
  },
});
